import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  Download,
  FileText,
  Inbox,
  LineChart,
  Loader2,
  Mail,
  MousePointerClick,
  OctagonAlert,
  ShieldCheck,
} from "lucide-react";
import { usePageList } from "@/hooks/use-pages";
import { usePageSubmissionAnalytics, usePageSubmissions } from "@/hooks/use-page-forms";

function formatSubmissionValue(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((entry) => formatSubmissionValue(entry)).join(", ");
  }
  if (value && typeof value === "object") {
    return JSON.stringify(value);
  }
  return "—";
}

function buildCsv(rows: Array<Record<string, string>>) {
  if (rows.length === 0) {
    return "";
  }

  const headers = Object.keys(rows[0]!);
  const escape = (value: string) => `"${value.replaceAll("\"", "\"\"")}"`;
  const lines = [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => escape(row[header] ?? "")).join(",")),
  ];
  return lines.join("\n");
}

export default function FormsPage() {
  const { pages, isLoading: isLoadingPages, error: pageError } = usePageList();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const pagesWithFormsContext = useMemo(
    () => pages.filter((page) => page.status === "published" || page.status === "draft"),
    [pages],
  );

  const effectivePageId = selectedPageId;
  const selectedPage = pagesWithFormsContext.find((page) => page.id === effectivePageId) ?? null;
  const submissionsQuery = usePageSubmissions(effectivePageId);
  const analyticsQuery = usePageSubmissionAnalytics(effectivePageId);
  const pageLookup = useMemo(
    () => new Map(pagesWithFormsContext.map((page) => [page.id, page])),
    [pagesWithFormsContext],
  );

  const handleExport = () => {
    if (!selectedPage || submissionsQuery.submissions.length === 0) {
      return;
    }

    const csv = buildCsv(submissionsQuery.submissions.map((submission) => ({
      submitted_at: submission.createdAt,
      contact_email: submission.contactEmail ?? "",
      referrer: submission.referrer ?? "",
      utm_source: submission.utmSource ?? "",
      utm_campaign: submission.utmCampaign ?? "",
      data: JSON.stringify(submission.data),
    })));

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedPage.slug}-submissions.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-muted-foreground">Submission Operations</p>
            <h1 className="text-2xl font-bold text-foreground">Published page submissions are now mounted on this route.</h1>
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              This inbox now reads the mounted pages submission contract instead of presenting a placeholder boundary page.
              Start with the full tenant inbox, then drill into a single page when you need local context or a page-specific export.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/pages/sites"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
            >
              Review page inventory
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={selectedPage ? `/pages/editor/${selectedPage.id}?mode=simple` : "/pages/editor/new"}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              Edit website
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h3 className="mt-4 text-base font-semibold text-foreground">Mounted truth</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Visitor submissions still enter through published pages, and this route now reads that stored submission data directly instead of faking an empty inbox.
          </p>
        </article>

        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <LineChart className="h-5 w-5 text-primary" />
          <h3 className="mt-4 text-base font-semibold text-foreground">Current analytics</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Submission totals, daily captures, and conversion rate now come from the mounted forms analytics contract backed by live page-view telemetry.
            The tenant-wide inbox and page-specific drilldown now read the same source of truth instead of blending frontend estimates.
          </p>
        </article>

        <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
          <Inbox className="h-5 w-5 text-primary" />
          <h3 className="mt-4 text-base font-semibold text-foreground">Current scope</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            This now closes the cross-page unified inbox for review plus per-page CSV export. Spam triage and reviewer assignment can come later without pretending they already exist.
          </p>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="rounded-[24px] border border-border bg-card p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Pages</p>
              <h2 className="mt-1 text-lg font-semibold text-foreground">Inbox scope</h2>
            </div>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </div>

          {isLoadingPages ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading pages…
            </div>
          ) : pageError ? (
            <p className="mt-4 rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">{pageError}</p>
          ) : pagesWithFormsContext.length === 0 ? (
            <p className="mt-4 rounded-2xl border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
              No pages yet. Publish a page with a form block to start collecting submissions.
            </p>
          ) : (
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={() => setSelectedPageId(null)}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                  effectivePageId === null ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-background hover:border-foreground/20"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-foreground">All pages</div>
                    <div className="mt-1 text-xs text-muted-foreground">Unified tenant inbox</div>
                  </div>
                  <span className="rounded-full border border-border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    {pagesWithFormsContext.length} pages
                  </span>
                </div>
              </button>
              {pagesWithFormsContext.map((page) => {
                const isActive = page.id === effectivePageId;
                return (
                  <button
                    key={page.id}
                    type="button"
                    onClick={() => setSelectedPageId(page.id)}
                    className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                      isActive ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-background hover:border-foreground/20"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-foreground">{page.title}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{page.slug}</div>
                      </div>
                      <span className="rounded-full border border-border px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                        {page.status}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        <div className="space-y-6">
          <section className="grid gap-4 md:grid-cols-3">
            <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <Inbox className="h-3.5 w-3.5" />
                Total submissions
              </div>
              <p className="mt-3 text-3xl font-semibold text-foreground">{analyticsQuery.analytics?.total ?? 0}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {selectedPage ? "Stored for the selected page." : "Stored across every mounted page inbox."}
              </p>
            </article>

            <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <MousePointerClick className="h-3.5 w-3.5" />
                Today
              </div>
              <p className="mt-3 text-3xl font-semibold text-foreground">{analyticsQuery.analytics?.today ?? 0}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {selectedPage ? "New submissions for the selected page today." : "New submissions across the tenant today."}
              </p>
            </article>

            <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <LineChart className="h-3.5 w-3.5" />
                Conversion rate
              </div>
              <p className="mt-3 text-3xl font-semibold text-foreground">
                {`${((analyticsQuery.analytics?.conversionRate ?? 0) * 100).toFixed(1)}%`}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {selectedPage
                  ? "Calculated from live page views for the published version of this page."
                  : "Calculated from live page views across the tenant-wide published page footprint."}
              </p>
            </article>

            <article className="rounded-[22px] border border-border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                <OctagonAlert className="h-3.5 w-3.5" />
                Abandonment
              </div>
              <p className="mt-3 text-3xl font-semibold text-foreground">
                {`${((analyticsQuery.analytics?.abandonmentRate ?? 0) * 100).toFixed(1)}%`}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {`${analyticsQuery.analytics?.abandonmentCount ?? 0} starts exited before completion in the current analytics scope.`}
              </p>
            </article>
          </section>

          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Field drop-off</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">
                  {selectedPage ? "Highest-friction fields on this page" : "Highest-friction fields across mounted forms"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Drop-off uses the stored field analytics seam and shows where visitors start but do not complete.
                </p>
              </div>
            </div>

            {analyticsQuery.isLoading ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading drop-off analytics…
              </div>
            ) : analyticsQuery.error ? (
              <p className="mt-6 rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {analyticsQuery.error}
              </p>
            ) : (analyticsQuery.analytics?.topDropOffFields?.length ?? 0) === 0 ? (
              <div className="mt-6 rounded-[24px] border border-dashed border-border px-6 py-10 text-center">
                <LineChart className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm font-medium text-foreground">No field drop-off data yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Field-level analytics will appear here once the mounted form analytics table receives tracked starts and completions.
                </p>
              </div>
            ) : (
              <div className="mt-6 overflow-hidden rounded-2xl border border-border">
                <div className="grid grid-cols-[1.2fr_110px_110px_130px_120px] gap-0 border-b border-border bg-muted/40 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  <span>Field</span>
                  <span>Starts</span>
                  <span>Done</span>
                  <span>Abandoned</span>
                  <span>Drop-off</span>
                </div>
                <div className="divide-y divide-border bg-background">
                  {analyticsQuery.analytics?.topDropOffFields.map((field) => (
                    <article
                      key={`${field.formId}:${field.fieldId}`}
                      className="grid grid-cols-[1.2fr_110px_110px_130px_120px] gap-0 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">{field.fieldId}</div>
                        <div className="mt-1 text-xs text-muted-foreground">Form {field.formId}</div>
                      </div>
                      <div className="text-foreground">{field.starts}</div>
                      <div className="text-foreground">{field.completions}</div>
                      <div className="text-foreground">{field.abandonmentCount}</div>
                      <div className="font-medium text-foreground">{`${(field.dropOffRate * 100).toFixed(1)}%`}</div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Recent submissions</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">
                  {selectedPage ? `${selectedPage.title} inbox` : "All pages inbox"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedPage
                    ? "Review the latest page captures and export the mounted records as CSV."
                    : "Review recent captures across every page, then switch to a page-specific view when you need a local export."}
                </p>
              </div>

              <button
                type="button"
                onClick={handleExport}
                disabled={!selectedPage || submissionsQuery.submissions.length === 0}
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
            </div>

            {submissionsQuery.isLoading ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading submissions…
              </div>
            ) : submissionsQuery.error ? (
              <p className="mt-6 rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {submissionsQuery.error}
              </p>
            ) : submissionsQuery.submissions.length === 0 ? (
              <div className="mt-6 rounded-[24px] border border-dashed border-border px-6 py-10 text-center">
                <Mail className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm font-medium text-foreground">No submissions yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedPage
                    ? "Publish a page with a contact or lead form and submissions will appear here."
                    : "Publish a page with a contact or lead form and tenant-wide submissions will appear here."}
                </p>
              </div>
            ) : (
              <div className="mt-6 overflow-hidden rounded-2xl border border-border">
                <div className={`grid gap-0 border-b border-border bg-muted/40 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground ${
                  selectedPage ? "grid-cols-[160px_180px_1fr]" : "grid-cols-[160px_180px_180px_1fr]"
                }`}>
                  <span>Received</span>
                  <span>Email</span>
                  {!selectedPage ? <span>Page</span> : null}
                  <span>Submitted data</span>
                </div>
                <div className="divide-y divide-border bg-background">
                  {submissionsQuery.submissions.map((submission) => (
                    <article
                      key={submission.id}
                      className={`grid gap-0 px-4 py-3 text-sm ${
                        selectedPage ? "grid-cols-[160px_180px_1fr]" : "grid-cols-[160px_180px_180px_1fr]"
                      }`}
                    >
                      <div className="text-muted-foreground">
                        {new Date(submission.createdAt).toLocaleString()}
                      </div>
                      <div className="font-medium text-foreground">
                        {submission.contactEmail ?? "—"}
                      </div>
                      {!selectedPage ? (
                        <div className="text-muted-foreground">
                          {pageLookup.get(submission.pageId)?.title ?? submission.pageId}
                        </div>
                      ) : null}
                      <div className="space-y-1">
                        {Object.entries(submission.data).slice(0, 5).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="min-w-24 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{key}</span>
                            <span className="text-foreground">{formatSubmissionValue(value)}</span>
                          </div>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}
